import React, { forwardRef, ComponentPropsWithRef } from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import type {
  ReactDefaultPropsWithChildren,
  ReactComponentStaticProps,
} from "../../types/react";

import countDescendants from "../../helper/countDescendants";

import { EqualHeight } from "../EqualHeight/EqualHeight";

import styles from "./ContentTileNavigation.scss";

import { NavTile } from "./NavTile";

export const ContentTileNavigationDefaultTagName = "div";
export type ContentTileNavigationDefaultTag = typeof ContentTileNavigationDefaultTagName;

type ContentTileNavigationBaseProps<
  Tag extends React.ElementType = ContentTileNavigationDefaultTag
> = {
  tag?: Tag;
};

export type ContentTileNavigationProps<
  Tag extends React.ElementType = ContentTileNavigationDefaultTag
> = ContentTileNavigationBaseProps<Tag> &
  Omit<
    React.ComponentPropsWithRef<Tag>,
    keyof ContentTileNavigationBaseProps<Tag>
  >;

const defaultProps = {
  children: "",
  tag: ContentTileNavigationDefaultTagName,
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  ContentTileNavigationBaseProps,
  typeof defaultProps
> = defaultProps;

const ContentTileNavigationWithRef = <
  Tag extends React.ElementType = ContentTileNavigationDefaultTag
>(
  props: ContentTileNavigationProps<Tag>,
  ref: ComponentPropsWithRef<Tag>["ref"]
) => {
  const {
    children,
    tag: CustomTag = validatedDefaultProps.tag,
    ...others
  } = props;
  const numTiles = countDescendants({ props: { children } }, [
    NavTile.displayName,
  ]);
  const tileNumberClasses = [
    undefined,
    "contentTileNavigation--two-tiles",
    "contentTileNavigation--three-tiles",
    "contentTileNavigation--four-tiles",
    "contentTileNavigation--five-tiles",
    undefined,
  ] as const;
  const tileNumberClass =
    numTiles > 0 && numTiles < 6
      ? tileNumberClasses[numTiles - 1]
      : tileNumberClasses[5];
  const classNames = cn(
    styles.contentTileNavigation,
    tileNumberClass && styles[tileNumberClass]
  );
  return (
    <CustomTag className={classNames} ref={ref} {...others}>
      <EqualHeight>{children}</EqualHeight>
    </CustomTag>
  );
};

export const ContentTileNavigation = (forwardRef(
  ContentTileNavigationWithRef
) as unknown) as (<
  Tag extends React.ElementType = ContentTileNavigationDefaultTag
>(
  props: ContentTileNavigationProps<Tag>
) => ReturnType<typeof ContentTileNavigationWithRef>) &
  ReactComponentStaticProps<ContentTileNavigationProps, "tag"> & {
    Item: typeof NavTile;
  };

ContentTileNavigation.propTypes = {
  tag: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

ContentTileNavigation.defaultProps = validatedDefaultProps;

ContentTileNavigation.Item = NavTile;

ContentTileNavigation.displayName = "ContentTileNavigation";

export default ContentTileNavigation;
